import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Botao, CampoDeSelecao, Modal } from '../../componentes/ui';
import { criarCard, type CardResumo, type ColunaDoQuadro } from '../../servicos/crm';
import type { EmpresaResumo } from '../../servicos/empresas';
import { ErroDaApi } from '../ErroDaApi';
import { SeletorDeEmpresa } from '../SeletorDeEmpresa';

/**
 * Põe uma empresa no quadro aberto. Uma vez por quadro: se ela já estiver
 * nele, a recusa do servidor diz em que coluna — e o operador vai até ela, em
 * vez de duplicar. Em outro quadro, ela pode estar à parte.
 */
export function NovoCard(props: {
  readonly aberto: boolean;
  readonly colunas: readonly ColunaDoQuadro[];
  /** A coluna em que se clicou "+ Adicionar empresa"; senão, a primeira. */
  readonly colunaInicial?: string;
  readonly aoFechar: () => void;
  readonly aoCriar: (card: CardResumo) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [empresa, setEmpresa] = useState<EmpresaResumo | null>(null);
  const [colunaId, setColunaId] = useState(props.colunaInicial ?? props.colunas[0]?.id ?? '');

  const criar = useMutation({
    mutationFn: () => criarCard(empresa?.id ?? '', colunaId === '' ? {} : { colunaId }),
    onSuccess: (card) => {
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      props.aoCriar(card);
    },
  });

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Adicionar empresa"
      descricao="Escolha a empresa que entra neste quadro. Cada empresa entra uma vez em cada quadro."
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao
            disabled={empresa === null}
            carregando={criar.isPending}
            onClick={() => criar.mutate()}
          >
            Adicionar ao quadro
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <SeletorDeEmpresa escolhida={empresa} aoEscolher={setEmpresa} />
        <CampoDeSelecao
          rotulo="Coluna"
          value={colunaId}
          onChange={(e) => setColunaId(e.target.value)}
          opcoes={props.colunas.map((c) => ({ valor: c.id, rotulo: c.nome }))}
        />
        {criar.isError && <ErroDaApi titulo="Não foi possível adicionar" erro={criar.error} />}
      </div>
    </Modal>
  );
}
